import React, { Dispatch, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import Select, { components } from 'react-select';
import '../../css/Modal.css';
import 'bootstrap/dist/css/bootstrap.css';
import { useAppDispatch } from '../../context/store';
import {
    CONFIRM_ADD_EMAIL_NOTIFICATION_GROUP_MESSAGE,
    DO_NOT_REMOVE_EMAIL_MESSAGE,
    DO_NOT_REMOVE_EMAIL_NOTIFICATIONS_LIST,
    EMAIL_GROUP_TYPES,
    EMAIL_REGEX,
    EVENT_STATUS_LIST,
    EVENT_STATUS_TYPE_SELECT_OPTIONS,
    INVALID_ABACUSID_MESSAGE,
    RP_BLUE,
    VALIDATING_MESSAGE
} from '../../lib/constants';
import { showConfirmationDialog } from '../Commons';
import { useSelector } from 'react-redux';
import { RootState } from '../../context/rootReducer';
import { addEmailNotificationGroupAsync } from '../../slices/admin/emailNotificationsSlice';
import { validateTeamIdAsync } from '../../slices/admin/rolePermissionsSlice';
import { EmailGroupType, ReactSelectOption } from '../../lib/types';

type Props = {
    isVisible: Dispatch<boolean>;
    groupType: EmailGroupType;
};

const EmailNotificationsGroup = ({ isVisible, groupType }: Props): JSX.Element => {
    const dispatch = useAppDispatch();

    const { validTeamId, isValidTeamIdLoading } = useSelector((state: RootState) => state.rolePermissions);
    const { userCardholderInformation } = useSelector((state: RootState) => state.userProfile);

    const [name, setName] = useState('');
    const [extId, setExtId] = useState('');
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [isDoNotRemoveEmail, setIsDoNotRemoveEmail] = useState(false);
    const [events, setEvents] = useState({ open: false, close: false, reopen: false, end: false });
    const [selectedOptions, setSelectedOptions] = useState<ReactSelectOption[]>([]);

    const handleSubmit = async (): Promise<void> => {
        const trimmedName = name.trim();
        setName(trimmedName);
        const confirmed = await showConfirmationDialog({
            title: CONFIRM_ADD_EMAIL_NOTIFICATION_GROUP_MESSAGE
        });
        if (confirmed) {
            dispatch(
                addEmailNotificationGroupAsync({
                    name: trimmedName,
                    extId,
                    groupType,
                    events,
                    updatedBy: userCardholderInformation.login
                })
            );
            isVisible(false);
        }
    };

    const Option = (props) => {
        return (
            <div>
                <components.Option {...props}>
                    <input
                        className='mb-3'
                        type='checkbox'
                        checked={props.isSelected}
                        onChange={() => null}
                    />
                    <label style={{ fontSize: '10pt', paddingLeft: 10 }}>{props.label}</label>
                </components.Option>
            </div>
        );
    };

    const customSelectStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            borderColor: '#667085',
            '&:hover': {
                borderColor: '#667085'
            },
            boxShadow: state.isFocused ? `0 0 0 1px ${RP_BLUE}` : 'none',
            fontSize: '14px'
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            fontSize: '14px'
        })
    };
    const handleEventStatusTypeChange = (options: ReactSelectOption[] = [], actionMeta: any): void => {
        const applySelection = (nextSelection: ReactSelectOption[]) => {
            setSelectedOptions(nextSelection);
            setEvents(
                EVENT_STATUS_LIST.reduce(
                    (acc, event) => {
                        acc[event] = nextSelection.some((option) => option.value === event);
                        return acc;
                    },
                    { open: false, close: false, reopen: false, end: false }
                )
            );
        };

        if (actionMeta.action === 'clear' || !Array.isArray(options)) {
            return applySelection([]);
        }

        if (actionMeta.option?.value === 'all') {
            const nextSelection =
                selectedOptions.length === EVENT_STATUS_LIST.length
                    ? []
                    : EVENT_STATUS_TYPE_SELECT_OPTIONS.filter((option) => option.value !== 'all');
            return applySelection(nextSelection);
        }

        applySelection(options.filter((option) => option.value !== 'all'));
    };

    const handleAbacusIdChange = (value: string): void => {
        setExtId(value.trim());
        if (value !== '' && value.trim() !== '') {
            dispatch(validateTeamIdAsync(value.trim(), 'team'));
        }
    };

    const handleEmailChange = (value: string): void => {
        setExtId(value.trim());
        setName(value.trim());
        setIsEmailValid(EMAIL_REGEX.test(value));
        setIsDoNotRemoveEmail(DO_NOT_REMOVE_EMAIL_NOTIFICATIONS_LIST.includes(value));
    };

    return (
        <Modal width='200px' centered show={isVisible} onHide={() => isVisible(false)}>
            <Modal.Header className='modalHeader' closeButton>
                <Modal.Title className='modalTitle'>
                    Add {groupType === EMAIL_GROUP_TYPES.TEAM ? 'Team' : 'Email List'} to Email Notification
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form className='formBody'>
                    <Form.Group controlId='inputEventStatus'>
                        <Form.Label className='formLabel'>Event Status</Form.Label>
                        <Select
                            styles={customSelectStyles}
                            options={EVENT_STATUS_TYPE_SELECT_OPTIONS}
                            isOptionSelected={(option, selectedValues) =>
                                option.value === 'all'
                                    ? selectedValues.length === EVENT_STATUS_LIST.length
                                    : selectedValues.some(
                                          (selectedOption) => selectedOption.value === option.value
                                      )
                            }
                            onChange={handleEventStatusTypeChange}
                            placeholder={
                                <Form.Text className='EventStatusPlaceholder'>
                                    Start, Complete, Reopen, End
                                </Form.Text>
                            }
                            value={selectedOptions}
                            components={{ Option, IndicatorSeparator: () => null }}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            isMulti
                        />
                    </Form.Group>
                    {groupType === EMAIL_GROUP_TYPES.EMAIL_LIST && (
                        <>
                            <Form.Group controlId='inputCountry'>
                                <Form.Label className='formLabel'>
                                    Email <span className='asterisk'>*</span>
                                </Form.Label>
                                <Form.Control
                                    className='formControl'
                                    as='input'
                                    value={extId}
                                    onChange={(event) => handleEmailChange(event.target.value)}
                                    placeholder='Enter Email'
                                    isValid={extId !== '' && isEmailValid && !isDoNotRemoveEmail}
                                    isInvalid={extId !== '' && (!isEmailValid || isDoNotRemoveEmail)}
                                />
                                {isDoNotRemoveEmail && (
                                    <Form.Control.Feedback type='invalid' style={{ fontSize: '9pt' }}>
                                        {DO_NOT_REMOVE_EMAIL_MESSAGE}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </>
                    )}
                    {groupType === EMAIL_GROUP_TYPES.TEAM && (
                        <>
                            <Form.Group controlId='inputCountry'>
                                <Form.Label className='formLabel'>
                                    Name <span className='asterisk'>*</span>
                                </Form.Label>
                                <Form.Control
                                    className='formControl'
                                    as='input'
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    placeholder='Enter name...'
                                />
                            </Form.Group>
                            <Form.Group controlId='inputCountry'>
                                <Form.Label className='formLabel'>
                                    Abacus ID <span className='asterisk'>*</span>
                                </Form.Label>
                                <Form.Control
                                    className='formControl'
                                    as='input'
                                    value={extId}
                                    onChange={(event) => handleAbacusIdChange(event.target.value)}
                                    placeholder='Enter Abacus ID'
                                    isValid={extId.trim() !== '' && validTeamId}
                                    isInvalid={extId.trim() !== '' && !validTeamId}
                                />
                                {isValidTeamIdLoading && (
                                    <Form.Control.Feedback type='invalid' style={{ fontSize: '9pt' }}>
                                        {VALIDATING_MESSAGE}
                                    </Form.Control.Feedback>
                                )}
                                {!isValidTeamIdLoading && (
                                    <Form.Control.Feedback type='invalid' style={{ fontSize: '9pt' }}>
                                        {INVALID_ABACUSID_MESSAGE + extId}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </>
                    )}
                    <Button
                        disabled={
                            !selectedOptions?.length ||
                            (groupType === EMAIL_GROUP_TYPES.EMAIL_LIST &&
                                (!extId || !isEmailValid || isDoNotRemoveEmail)) ||
                            (groupType === EMAIL_GROUP_TYPES.TEAM &&
                                (!name?.trim().length || !extId || !validTeamId))
                        }
                        className='my-2 saveButton'
                        onClick={handleSubmit}
                    >
                        Add Group
                    </Button>
                    <Button
                        className='mb-2 deleteCancelButton'
                        style={{ color: RP_BLUE }}
                        onClick={() => isVisible(false)}
                    >
                        Cancel
                    </Button>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default EmailNotificationsGroup;
